import React, { useMemo } from 'react';
import { SelectInput } from '../FormControls';
import { useAgeGroupSelector } from './useAgeGroupSelector';

/**
 * Age group selector: dropdown select for choosing age groups.
 * Uses age groups from the admin module.
 *
 * @param {Object} props
 * @param {string} props.id - Input id
 * @param {string} [props.label] - Label
 * @param {boolean} [props.required]
 * @param {string} [props.error]
 * @param {Object|string} props.value - Current selection: { ageGroup, displayText, ... } or string (ageGroup ID)
 * @param {function(Object|null): void} props.onChange - Called with { ageGroup, displayText } or null
 * @param {string} [props.placeholder] - Placeholder when nothing selected
 * @param {number} [props.labelWidthRem]
 * @param {'top'|'left'} [props.labelPosition]
 * @param {boolean} [props.disabled]
 */
export function AgeGroupSelector({
  id,
  label,
  required = false,
  error,
  value = '',
  onChange,
  placeholder = 'Select age group...',
  labelWidthRem = 8,
  labelPosition = 'top',
  disabled = false,
}) {
  const { ageGroups, loading } = useAgeGroupSelector();

  // Resolve current value to ageGroup ID
  const currentValue = useMemo(() => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value.ageGroup) {
      return value.ageGroup._id ?? value.ageGroup.id ?? '';
    }
    return '';
  }, [value]);

  const handleChange = (e) => {
    const selectedId = e.target.value;
    if (!selectedId) {
      onChange?.(null);
      return;
    }

    const selectedAgeGroup = ageGroups.find(
      (ag) => (ag._id ?? ag.id) === selectedId
    );

    if (selectedAgeGroup) {
      const name = selectedAgeGroup.name ?? '';
      const code = selectedAgeGroup.code ?? '';
      const displayText = code && name ? `${code}: ${name}` : code || name;

      onChange?.({
        ageGroup: selectedAgeGroup,
        displayText,
      });
    }
  };

  return (
    <SelectInput
      id={id}
      label={label}
      required={required}
      error={error}
      value={currentValue}
      onChange={handleChange}
      disabled={disabled || loading}
      labelWidthRem={labelWidthRem}
      labelPosition={labelPosition}
    >
      <option value="">{loading ? 'Loading...' : placeholder}</option>
      {ageGroups.map((ageGroup) => {
        const ageGroupId = ageGroup._id ?? ageGroup.id ?? '';
        const name = ageGroup.name ?? '';
        const code = ageGroup.code ?? '';
        const displayLabel = code && name ? `${code}: ${name}` : code || name;

        return (
          <option key={ageGroupId} value={ageGroupId}>
            {displayLabel}
          </option>
        );
      })}
    </SelectInput>
  );
}

export default AgeGroupSelector;
